import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { toast } from "sonner";
import { formatDate, formatDateTime, formatCurrency } from "@/lib/format";
import HotelBillingTab from "./HotelBillingTab";
import {
    Building2, BedDouble, UsersRound, CreditCard, Plug, Clock, ArrowLeft, LogIn,
    MoreVertical, Pause, Play, Archive, Trash2, Mail, Phone, Globe2, MapPin, User as UserIcon,
    Loader2, RefreshCw, Calendar as CalIcon, Wallet, ShieldCheck, CircleAlert, Settings, Save,
    ToggleLeft, ToggleRight, AlertTriangle, DollarSign,
} from "lucide-react";

const TABS = [
    { id: "overview", label: "Overview", icon: Building2 },
    { id: "rooms", label: "Rooms & inventory", icon: BedDouble },
    { id: "users", label: "Users", icon: UsersRound },
    { id: "billing", label: "Billing & Modules", icon: DollarSign },
    { id: "subscription", label: "Legacy Sub", icon: CreditCard },
    { id: "integrations", label: "Integrations", icon: Plug },
    { id: "activity", label: "Activity log", icon: Clock },
    { id: "settings", label: "Settings", icon: Settings },
];

const STATUS_COLORS = {
    active: "bg-emerald-50 text-emerald-700",
    trial: "bg-sky-50 text-sky-700",
    pending_setup: "bg-amber-50 text-amber-700",
    past_due: "bg-orange-50 text-orange-700",
    suspended: "bg-red-50 text-red-700",
    archived: "bg-slate-100 text-slate-600",
};

export default function SuperHotelDetail() {
    const { hotelId } = useParams();
    const navigate = useNavigate();
    const [tab, setTab] = useState("overview");
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const t = searchParams.get("tab");
        if (t) setTab(t);
    }, [searchParams]);
    const [hotel, setHotel] = useState(null);
    const [loading, setLoading] = useState(false);
    const [plans, setPlans] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const [h, p] = await Promise.all([
                api.get(`/superadmin/hotels/${hotelId}`),
                api.get("/billing/plans").catch(() => ({ data: [] })),
            ]);
            setHotel(h.data);
            setPlans(p.data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [hotelId]);

    if (loading && !hotel) return (
        <div className="flex-1 p-8"><Loader2 className="animate-spin text-[#F26322]" /></div>
    );
    if (!hotel) return null;

    const doAction = async (endpoint, body = null, msg = "Done") => {
        try {
            if (body) await api.post(`/superadmin/hotels/${hotelId}/${endpoint}`, body);
            else await api.post(`/superadmin/hotels/${hotelId}/${endpoint}`);
            toast.success(msg);
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const impersonate = async () => {
        const reason = window.prompt(`Open ${hotel.name} as its hotel admin. Reason:`);
        if (!reason || reason.trim().length < 3) return;
        try {
            const { data: users } = await api.get(`/superadmin/hotels/${hotelId}/users`);
            const admin = users.find((u) => u.role === "hotel_admin");
            if (!admin) { toast.error("No hotel admin found"); return; }
            // Preserve the current super_admin token BEFORE the swap so the
            // ImpersonationBanner "Return to Super Admin" button can restore it.
            const origToken = localStorage.getItem("akm_token");
            const { data } = await api.post(`/superadmin/users/${admin.id}/impersonate?reason=${encodeURIComponent(reason)}`);
            if (data.access_token) {
                if (origToken) localStorage.setItem("akm_imp_orig_token", origToken);
                localStorage.setItem("akm_imp_orig_user", JSON.stringify({ role: "super_admin" }));
                localStorage.setItem("akm_imp_token", data.access_token);
                localStorage.setItem("akm_token", data.access_token);
            }
            toast.success(`Impersonating ${admin.name || admin.email}`);
            navigate("/dashboard");
            setTimeout(() => window.location.reload(), 150);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <>
            <Topbar title={hotel.name} subtitle={`${hotel.city || ""}${hotel.city && hotel.country ? ", " : ""}${hotel.country || ""} · ${hotel.slug}`} />
            <div className="flex-1 overflow-auto p-6" data-testid="super-hotel-detail">
                {/* Back + header actions */}
                <div className="mb-4 flex flex-wrap items-center gap-2">
                    <button onClick={() => navigate("/admin/hotels")} className="akm-btn-ghost h-9 text-[12.5px]">
                        <ArrowLeft size={13} /> Back to hotels
                    </button>
                    <span className={`ml-2 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${STATUS_COLORS[hotel.status] || "bg-slate-100 text-slate-600"}`}>
                        {(hotel.status || "unknown").replace(/_/g, " ")}
                    </span>
                    {hotel.suspension_reason && (
                        <div className="ml-2 text-[11.5px] italic text-red-700">Reason: {hotel.suspension_reason}</div>
                    )}
                    <div className="ml-auto flex flex-wrap gap-2">
                        <button onClick={impersonate} data-testid="hotel-impersonate" className="akm-btn-ghost h-9 text-[12.5px]">
                            <LogIn size={13} /> Open as Hotel Admin
                        </button>
                        <button onClick={load} className="akm-btn-ghost h-9"><RefreshCw size={12} /></button>
                        {hotel.status !== "suspended" && hotel.status !== "archived" && (
                            <button
                                onClick={async () => {
                                    const r = window.prompt("Reason for suspension:");
                                    if (r && r.trim().length >= 3) await doAction("suspend", { reason: r.trim() }, "Suspended");
                                }}
                                className="h-9 rounded-xl border border-amber-200 bg-amber-50 px-3 text-[12.5px] font-semibold text-amber-700 hover:bg-amber-100"
                            >
                                <Pause size={13} className="inline" /> Suspend
                            </button>
                        )}
                        {hotel.status === "suspended" && (
                            <button onClick={() => doAction("reactivate", null, "Reactivated")} className="h-9 rounded-xl border border-emerald-200 bg-emerald-50 px-3 text-[12.5px] font-semibold text-emerald-700 hover:bg-emerald-100">
                                <Play size={13} className="inline" /> Reactivate
                            </button>
                        )}
                        {hotel.status !== "archived" && (
                            <button
                                onClick={() => window.confirm(`Archive ${hotel.name}?`) && doAction("archive", null, "Archived")}
                                className="akm-btn-ghost h-9 text-[12.5px]"
                            ><Archive size={13} /> Archive</button>
                        )}
                        {hotel.status === "archived" && (
                            <button onClick={() => doAction("restore", null, "Restored")} className="h-9 rounded-xl border border-emerald-200 bg-emerald-50 px-3 text-[12.5px] font-semibold text-emerald-700 hover:bg-emerald-100">
                                <Play size={13} className="inline" /> Restore
                            </button>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <div className="mb-4 flex flex-wrap gap-1 border-b border-slate-200">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`hotel-tab-${t.id}`}
                            className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] font-medium transition ${
                                tab === t.id
                                    ? "border-[#F26322] text-[#F26322]"
                                    : "border-transparent text-slate-500 hover:text-slate-800"
                            }`}
                        >
                            <t.icon size={13} /> {t.label}
                        </button>
                    ))}
                </div>

                {tab === "overview" && <OverviewTab hotel={hotel} />}
                {tab === "rooms" && <RoomsTab hotelId={hotelId} />}
                {tab === "users" && <UsersTab hotelId={hotelId} />}
                {tab === "billing" && <HotelBillingTab hotelId={hotelId} />}
                {tab === "subscription" && <SubscriptionTab hotel={hotel} plans={plans} onAction={doAction} />}
                {tab === "integrations" && <IntegrationsTab hotel={hotel} onReload={load} />}
                {tab === "activity" && <ActivityTab hotelId={hotelId} />}
                {tab === "settings" && <SettingsTab hotel={hotel} onSaved={load} />}
            </div>
        </>
    );
}

function OverviewTab({ hotel }) {
    const kpi = [
        { l: "Total rooms", v: hotel.rooms_count ?? 0, icon: BedDouble },
        { l: "Active rooms", v: hotel.active_rooms ?? 0, icon: BedDouble },
        { l: "Room types", v: hotel.room_types_count ?? 0, icon: BedDouble },
        { l: "Active users", v: hotel.users_count ?? 0, icon: UsersRound },
        { l: "Reservations (30d)", v: hotel.reservations_30d ?? 0, icon: CalIcon },
        { l: "Revenue (30d)", v: formatCurrency(hotel.revenue_30d || 0, hotel.plan_currency || "EUR"), icon: Wallet },
    ];
    return (
        <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                {kpi.map((k) => (
                    <div key={k.l} className="akm-card p-3">
                        <div className="flex items-center justify-between">
                            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{k.l}</div>
                            <k.icon size={14} className="text-[#F26322]" />
                        </div>
                        <div className="mt-1 text-xl font-bold tabular-nums text-slate-900">{k.v}</div>
                    </div>
                ))}
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div className="akm-card p-5">
                    <div className="akm-section-title mb-3">Property details</div>
                    <Info label="Name" value={hotel.name} />
                    <Info label="Slug" value={hotel.slug} />
                    <Info label="Contact email" value={hotel.contact_email} icon={Mail} />
                    <Info label="Phone" value={hotel.phone} icon={Phone} />
                    <Info label="Country" value={hotel.country} icon={Globe2} />
                    <Info label="City" value={hotel.city} icon={MapPin} />
                    <Info label="Created" value={hotel.created_at ? formatDateTime(hotel.created_at) : "—"} />
                    <Info label="Last activity" value={hotel.last_activity_at ? formatDateTime(hotel.last_activity_at) : "—"} />
                </div>
                <div className="akm-card p-5">
                    <div className="akm-section-title mb-3">Owner & plan</div>
                    {hotel.owner ? (
                        <>
                            <Info label="Owner" value={hotel.owner.name} icon={UserIcon} />
                            <Info label="Email" value={hotel.owner.email} icon={Mail} />
                            <Info label="Last login" value={hotel.owner.last_login_at ? formatDateTime(hotel.owner.last_login_at) : "Never"} />
                        </>
                    ) : <div className="text-[13px] text-muted-foreground">No owner recorded.</div>}
                    <div className="my-3 border-t border-slate-100" />
                    <Info label="Plan" value={hotel.plan_name || "—"} />
                    <Info label="Subscription" value={hotel.subscription_status || "—"} />
                    <Info label="Integrations" value={`${hotel.integrations?.length || 0} configured`} />
                </div>
            </div>
        </>
    );
}

function RoomsTab({ hotelId }) {
    const [data, setData] = useState(null);
    useEffect(() => { api.get(`/superadmin/hotels/${hotelId}/rooms`).then(({ data }) => setData(data)); }, [hotelId]);
    if (!data) return <Loader />;
    return (
        <div className="space-y-4">
            <div className="akm-card p-4">
                <div className="akm-section-title mb-3">Room categories · {data.types.length}</div>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {data.types.map((t) => (
                        <div key={t.id} className="relative overflow-hidden rounded-xl border border-slate-200 p-3">
                            <div className="absolute inset-y-0 left-0 w-1" style={{ background: t.color || "#94a3b8" }} />
                            <div className="ml-2 flex items-center justify-between">
                                <div>
                                    <div className="text-[13.5px] font-semibold">{t.name}</div>
                                    <div className="text-[11px] text-muted-foreground">
                                        {t.capacity_adults || 2} adults · {t.capacity_children || 0} children · €{t.base_price || 0}/night
                                    </div>
                                </div>
                                {t.code && <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10.5px] font-bold">{t.code}</span>}
                            </div>
                        </div>
                    ))}
                    {data.types.length === 0 && <div className="col-span-full text-[13px] text-muted-foreground">No categories.</div>}
                </div>
            </div>
            <div className="akm-card p-4">
                <div className="akm-section-title mb-3">Physical rooms · {data.rooms.length}</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-[13px]">
                        <thead>
                            <tr className="border-b border-slate-100 bg-slate-50/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                                <th className="px-3 py-2 text-left font-medium">Number</th>
                                <th className="px-3 py-2 text-left font-medium">Floor</th>
                                <th className="px-3 py-2 text-left font-medium">Category</th>
                                <th className="px-3 py-2 text-left font-medium">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rooms.map((r) => (
                                <tr key={r.id} className="border-b border-slate-50">
                                    <td className="px-3 py-2 font-semibold tabular-nums">{r.number}</td>
                                    <td className="px-3 py-2">{r.floor ?? "—"}</td>
                                    <td className="px-3 py-2">
                                        <span className="inline-flex items-center gap-1">
                                            {r.room_type_color && <span className="h-2 w-2 rounded-full" style={{ background: r.room_type_color }} />}
                                            {r.room_type || "—"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-muted-foreground">{r.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function UsersTab({ hotelId }) {
    const [rows, setRows] = useState(null);
    useEffect(() => { api.get(`/superadmin/hotels/${hotelId}/users`).then(({ data }) => setRows(data)); }, [hotelId]);
    if (!rows) return <Loader />;
    return (
        <div className="akm-card overflow-hidden">
            <table className="w-full text-[13px]">
                <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/60 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <th className="px-3 py-2 text-left font-medium">User</th>
                        <th className="px-3 py-2 text-left font-medium">Email</th>
                        <th className="px-3 py-2 text-left font-medium">Role</th>
                        <th className="px-3 py-2 text-left font-medium">Status</th>
                        <th className="px-3 py-2 text-left font-medium">Last login</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((u) => (
                        <tr key={u.id} className="border-b border-slate-50">
                            <td className="px-3 py-2 font-semibold">{u.name || "—"}</td>
                            <td className="px-3 py-2 text-slate-600">{u.email}</td>
                            <td className="px-3 py-2">
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-semibold text-slate-700">{u.role}</span>
                            </td>
                            <td className="px-3 py-2">
                                {u.is_active
                                    ? <span className="text-emerald-700">active</span>
                                    : <span className="text-red-600">disabled</span>}
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">{u.last_login_at ? formatDateTime(u.last_login_at) : "—"}</td>
                        </tr>
                    ))}
                    {rows.length === 0 && (
                        <tr><td colSpan={5} className="px-3 py-12 text-center text-muted-foreground">No users assigned to this hotel.</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

function SubscriptionTab({ hotel, plans, onAction }) {
    const [newPlan, setNewPlan] = useState(hotel.plan_id || "");
    const [days, setDays] = useState(14);
    const sub = hotel.subscription || {};
    return (
        <div className="grid gap-4 md:grid-cols-2">
            <div className="akm-card p-5">
                <div className="akm-section-title mb-3">Current subscription</div>
                <Info label="Plan" value={hotel.plan_name || "—"} />
                <Info label="Price" value={hotel.plan_price != null ? `${formatCurrency(hotel.plan_price, hotel.plan_currency || "EUR")}/mo` : "—"} />
                <Info label="Subscription status" value={hotel.subscription_status || "—"} />
                <Info label="Started at" value={sub.started_at ? formatDate(sub.started_at) : "—"} />
                <Info label="Trial ends" value={sub.trial_ends_at ? formatDate(sub.trial_ends_at) : "—"} />
                <Info label="Next billing" value={sub.renews_at ? formatDate(sub.renews_at) : "—"} />
                <Info label="Cancelled" value={sub.cancelled_at ? formatDate(sub.cancelled_at) : "—"} />
            </div>
            <div className="akm-card p-5">
                <div className="akm-section-title mb-3">Actions</div>
                <div className="space-y-3">
                    <div>
                        <div className="mb-1 text-[11.5px] font-semibold uppercase tracking-wider text-slate-500">Change plan</div>
                        <div className="flex gap-2">
                            <select
                                data-testid="sub-plan-select"
                                className="akm-input flex-1"
                                value={newPlan}
                                onChange={(e) => setNewPlan(e.target.value)}
                            >
                                <option value="">Select plan…</option>
                                {plans.map((p) => <option key={p.id} value={p.id}>{p.name} · €{p.price_monthly}/mo</option>)}
                            </select>
                            <button
                                onClick={() => newPlan && onAction("change-plan", { plan_id: newPlan }, "Plan updated")}
                                data-testid="sub-change-plan"
                                disabled={!newPlan || newPlan === hotel.plan_id}
                                className="akm-btn-primary h-10 disabled:opacity-40"
                            >
                                Change
                            </button>
                        </div>
                    </div>
                    <div>
                        <div className="mb-1 text-[11.5px] font-semibold uppercase tracking-wider text-slate-500">Extend trial (days)</div>
                        <div className="flex gap-2">
                            <input
                                data-testid="sub-trial-days" type="number" min={1} max={180}
                                className="akm-input flex-1"
                                value={days}
                                onChange={(e) => setDays(Number(e.target.value))}
                            />
                            <button
                                onClick={() => onAction("extend-trial", { days }, `Trial extended by ${days} days`)}
                                data-testid="sub-extend-trial"
                                className="akm-btn-ghost h-10"
                            >Extend</button>
                        </div>
                    </div>
                    <div className="border-t border-slate-100 pt-3">
                        <div className="flex flex-wrap gap-2">
                            {hotel.subscription_status !== "paused" && hotel.subscription_status !== "cancelled" && (
                                <button
                                    onClick={() => onAction("pause-subscription", null, "Paused")}
                                    data-testid="sub-pause"
                                    className="h-9 rounded-xl border border-amber-200 bg-amber-50 px-3 text-[12.5px] font-semibold text-amber-700"
                                ><Pause size={12} className="inline" /> Pause</button>
                            )}
                            {(hotel.subscription_status === "paused" || hotel.subscription_status === "cancelled") && (
                                <button
                                    onClick={() => onAction("reactivate-subscription", null, "Reactivated")}
                                    data-testid="sub-reactivate"
                                    className="h-9 rounded-xl border border-emerald-200 bg-emerald-50 px-3 text-[12.5px] font-semibold text-emerald-700"
                                ><Play size={12} className="inline" /> Reactivate</button>
                            )}
                            {hotel.subscription_status !== "cancelled" && (
                                <button
                                    onClick={() => window.confirm("Cancel subscription?") && onAction("cancel-subscription", null, "Cancelled")}
                                    data-testid="sub-cancel"
                                    className="h-9 rounded-xl border border-red-200 bg-red-50 px-3 text-[12.5px] font-semibold text-red-700"
                                ><Trash2 size={12} className="inline" /> Cancel</button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function IntegrationsTab({ hotel, onReload }) {
    const integrations = hotel.integrations || [];
    const [busyId, setBusyId] = useState(null);
    const toggle = async (i) => {
        setBusyId(i.id);
        try {
            await api.post(`/superadmin/hotels/${hotel.id}/integrations/${i.id}/toggle`);
            toast.success(`Integration ${i.provider || i.name} toggled`);
            await onReload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusyId(null); }
    };
    return (
        <div className="akm-card overflow-hidden" data-testid="integrations-tab">
            <div className="border-b border-slate-100 bg-slate-50/60 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {integrations.length} integration{integrations.length === 1 ? "" : "s"}
            </div>
            {integrations.length === 0 ? (
                <div className="p-8 text-center text-[13px] text-muted-foreground">No integrations configured yet.</div>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {integrations.map((i) => {
                        const active = i.status === "connected" || i.enabled;
                        return (
                            <li key={i.id || i.provider} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`integration-row-${i.id || i.provider}`}>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[13.5px] font-semibold">{i.provider || i.name || "—"}</div>
                                    <div className="text-[11px] text-muted-foreground">{i.type || i.kind || ""}</div>
                                </div>
                                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${
                                    active ? "bg-emerald-50 text-emerald-700"
                                        : i.status === "error" ? "bg-red-50 text-red-700"
                                            : i.status === "pending" ? "bg-amber-50 text-amber-700"
                                                : "bg-slate-100 text-slate-600"
                                }`}>{i.status || (active ? "connected" : "disconnected")}</span>
                                {i.id && (
                                    <button
                                        onClick={() => toggle(i)}
                                        disabled={busyId === i.id}
                                        data-testid={`integration-toggle-${i.id}`}
                                        className="shrink-0 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                                        title={active ? "Disable" : "Enable"}
                                    >
                                        {active ? <ToggleRight size={18} className="text-emerald-500" /> : <ToggleLeft size={18} />}
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

function SettingsTab({ hotel, onSaved }) {
    const [form, setForm] = useState({
        name: hotel.name || "",
        legal_name: hotel.legal_name || "",
        contact_email: hotel.contact_email || "",
        phone: hotel.phone || "",
        country: hotel.country || "",
        city: hotel.city || "",
        postal_code: hotel.postal_code || "",
        address: hotel.address || "",
        timezone: hotel.timezone || "Europe/Tirane",
        currency: hotel.currency || "EUR",
        language: hotel.language || "sq",
        // ─── Fiscal / legal identification ───
        nipt: hotel.nipt || "",
        vat_id: hotel.vat_id || "",
        business_reg_no: hotel.business_reg_no || "",
        iban: hotel.iban || "",
        swift_bic: hotel.swift_bic || "",
        bank_name: hotel.bank_name || "",
        invoice_footer: hotel.invoice_footer || "",
    });
    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/superadmin/hotels/${hotel.id}/settings`, form);
            toast.success("Settings saved");
            await onSaved();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setSaving(false); }
    };
    const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
    const Section = ({ title, hint, fields }) => (
        <div className="mb-6">
            <div className="mb-1 text-[12px] font-semibold uppercase tracking-wider text-slate-700">{title}</div>
            {hint && <div className="mb-3 text-[11px] text-muted-foreground">{hint}</div>}
            <div className="grid gap-3 md:grid-cols-2">
                {fields.map(([k, label, placeholder, cls]) => (
                    <div key={k} className={cls || ""}>
                        <div className="mb-1 text-[11.5px] font-semibold text-slate-500">{label}</div>
                        <input
                            data-testid={`settings-${k}`}
                            className="akm-input"
                            value={form[k]}
                            placeholder={placeholder || ""}
                            onChange={(e) => setField(k, e.target.value)}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
    return (
        <div className="akm-card p-5" data-testid="settings-tab">
            <div className="akm-section-title mb-3">Property settings</div>

            <Section title="Basics" fields={[
                ["name", "Trade name (as displayed to guests)"],
                ["legal_name", "Legal name (as on invoices)"],
                ["contact_email", "Contact email", "info@yourhotel.al"],
                ["phone", "Phone", "+355 …"],
            ]} />

            <Section title="Address" fields={[
                ["country", "Country", "Albania"],
                ["city", "City"],
                ["postal_code", "Postal code"],
                ["address", "Street address", "", "md:col-span-2"],
            ]} />

            <Section title="Fiscal identification"
                     hint="Shown on invoices, required for fiscalization and VAT reporting."
                     fields={[
                         ["nipt", "NIPT (Albania)", "e.g. K12345678A"],
                         ["vat_id", "VAT number (EU)", "e.g. IT01234567890"],
                         ["business_reg_no", "Business registration No.", "e.g. QKR NRB…"],
                         ["invoice_footer", "Invoice footer note (optional)", "", "md:col-span-2"],
                     ]} />

            <Section title="Bank details (optional — for wire transfers on invoices)"
                     fields={[
                         ["iban", "IBAN", "AL47 …"],
                         ["swift_bic", "SWIFT/BIC", "STANALTR…"],
                         ["bank_name", "Bank name", "Raiffeisen Bank Albania", "md:col-span-2"],
                     ]} />

            <Section title="Locale" fields={[
                ["timezone", "Timezone", "Europe/Tirane"],
                ["currency", "Currency", "EUR / ALL / USD"],
                ["language", "Language", "sq / en"],
            ]} />

            <div className="mt-5 flex justify-end">
                <button
                    onClick={save} disabled={saving}
                    data-testid="settings-save"
                    className="akm-btn-primary h-10 disabled:opacity-50"
                >
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                    Save changes
                </button>
            </div>
        </div>
    );
}

function ActivityTab({ hotelId }) {
    const [rows, setRows] = useState(null);
    useEffect(() => { api.get(`/superadmin/hotels/${hotelId}/activity`).then(({ data }) => setRows(data)); }, [hotelId]);
    if (!rows) return <Loader />;
    return (
        <div className="akm-card overflow-hidden">
            {rows.length === 0 ? (
                <div className="p-8 text-center text-[13px] text-muted-foreground">No activity yet.</div>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {rows.map((r) => (
                        <li key={r.id} className="flex items-start gap-3 px-4 py-3">
                            <div className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-[#F26322]" />
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-baseline gap-2">
                                    <span className="text-[12.5px] font-semibold text-slate-900">{r.action?.replace(/_/g, " ")}</span>
                                    <span className="text-[11px] text-muted-foreground">by {r.user_email || r.user_id || "system"}</span>
                                    <span className="ml-auto text-[10.5px] text-muted-foreground">{formatDateTime(r.created_at)}</span>
                                </div>
                                {r.metadata && Object.keys(r.metadata).length > 0 && (
                                    <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                                        {JSON.stringify(r.metadata)}
                                    </div>
                                )}
                                {r.ip && <div className="text-[10px] text-muted-foreground">IP {r.ip}</div>}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function Info({ label, value, icon: Icon }) {
    return (
        <div className="mb-2 flex items-center gap-2 text-[13px]">
            {Icon && <Icon size={13} className="shrink-0 text-slate-400" />}
            <span className="text-slate-500">{label}:</span>
            <span className="ml-auto truncate font-medium text-slate-900">{value || "—"}</span>
        </div>
    );
}

function Loader() {
    return <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin text-[#F26322]" /></div>;
}
